import React, { useState } from 'react';
import { 
  FaTimes, FaLink, FaFacebook, FaWhatsapp, FaFacebookMessenger, 
  FaLinkedin, FaEnvelope, FaCopy, FaRupeeSign, FaPhoneAlt, FaStar,
  FaCheckCircle, FaRegCopy, FaTwitter
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ShareProfileModal = ({ isOpen, onClose, profile }) => {
  const [isCopied, setIsCopied] = useState(false);
  // Handle both localhost and production URLs
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const origin = isLocalhost 
    ? window.location.origin 
    : window.location.origin.replace(/^http:/, 'https:');
  const profileType = profile?.profileType || 'professional';
  const profileUrl = `${origin}/${profileType}/${profile?.id}`;
  
  // Create a share message with the URL
  const shareMessage = `Check out ${profile?.fullName}'s profile on ASDFlo: ${profileUrl}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileUrl);
    setIsCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleShare = async (platform) => {
    const url = encodeURIComponent(profileUrl);
    // Use the formatted share message
    const message = `Check out ${profile?.fullName}'s profile on ASDFlo: ${profileUrl}`;
    
    // Create WhatsApp message with the same prefilled message as the profile button
    const whatsappMessage = `Hi ASDFlo Team, I'd like to connect with ${profile?.fullName} (ID: ${profile?.id}).`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent(message)}`,
      whatsapp: `https://wa.me/918978733122?text=${encodeURIComponent(whatsappMessage)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${encodeURIComponent(`Check out ${profile?.fullName}'s profile on ASDFlo`)}&body=${encodeURIComponent(profileUrl)}`
    };

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.fullName} on ASDFlo`,
          text: message,
          url: profileUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  if (!isOpen || !profile) return null;

  const handleShareClick = handleShare; // Use the same handler for consistency

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-sm relative shadow-2xl overflow-hidden max-h-[70vh] flex flex-col">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-base font-semibold">Share Profile</h2>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1 -mr-2"
              aria-label="Close"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        {/* Profile Info - Made more compact */}
        <div className="p-3">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img 
                src={profile.photoURL || '/default-profile.png'} 
                alt={profile.fullName} 
                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
              />
              <div className="absolute -bottom-1 -right-1 bg-yellow-400 text-yellow-800 rounded-full p-0.5">
                <FaStar className="text-[10px]" />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{profile.fullName}</h3>
              {Array.isArray(profile.expertise) && profile.expertise.length > 0 && (
                <p className="text-xs text-gray-500">
                  {profile.expertise[0]}
                  {profile.expertise.length > 1 ? ' • +' + (profile.expertise.length - 1) : ''}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Share Options - Made more compact */}
        <div className="px-4 pb-4 pt-2">
          <div className="grid grid-cols-4 gap-2">
            {/* WhatsApp */}
            <button
              onClick={() => handleShareClick('whatsapp')}
              className="flex flex-col items-center justify-center p-2 rounded-lg bg-green-50 hover:bg-green-100 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                <FaWhatsapp className="text-green-600 text-base" />
              </div>
              <span className="text-[11px] text-gray-600 mt-1">WhatsApp</span>
            </button>

            {/* Facebook */}
            <button
              onClick={() => handleShareClick('facebook')}
              className="flex flex-col items-center justify-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                <FaFacebook className="text-blue-600 text-base" />
              </div>
              <span className="text-[11px] text-gray-600 mt-1">Facebook</span>
            </button>

            {/* Copy Link */}
            <button
              onClick={copyToClipboard}
              className={`flex flex-col items-center justify-center p-2 rounded-lg ${isCopied ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'} transition-all`}
            >
              <div className={`w-9 h-9 rounded-full ${isCopied ? 'bg-green-100' : 'bg-gray-100'} flex items-center justify-center`}>
                {isCopied ? (
                  <FaCheckCircle className="text-green-500 text-base" />
                ) : (
                  <FaRegCopy className="text-gray-600 text-base" />
                )}
              </div>
              <span className={`text-[11px] mt-1 ${isCopied ? 'text-green-600' : 'text-gray-600'}`}>
                {isCopied ? 'Copied!' : 'Copy Link'}
              </span>
            </button>

            {/* More Options */}
            <div className="relative group">
              <button className="w-full h-full flex flex-col items-center justify-center p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                  </svg>
                </div>
                <span className="text-[11px] text-gray-600 mt-1">More</span>
              </button>
              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-10 overflow-hidden">
                <button
                  onClick={() => handleShareClick('linkedin')}
                  className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <FaLinkedin className="text-blue-600 text-base" />
                  <span>LinkedIn</span>
                </button>
                <button
                  onClick={() => handleShareClick('twitter')}
                  className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <FaTwitter className="text-blue-400 text-base" />
                  <span>Twitter</span>
                </button>
                <button
                  onClick={() => handleShareClick('email')}
                  className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <FaEnvelope className="text-gray-600 text-base" />
                  <span>Email</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Link Preview */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Profile Link</p>
            <div className="flex items-center">
              <input
                type="text"
                readOnly
                value={profileUrl}
                className="flex-1 text-sm bg-white border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent truncate"
              />
              <button
                onClick={copyToClipboard}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-r-lg transition-colors flex items-center justify-center"
              >
                {isCopied ? <FaCheckCircle className="text-green-500" /> : <FaRegCopy />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProfileModal;
